// components/EditVipDatePickerStep.jsx
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import moment from "moment-jalaali";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Lock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  XCircle,
  Clock,
} from "lucide-react";
import { toast } from "react-toastify";

const persianMonths = [
  { id: 1, name: "فروردین", days: 31 },
  { id: 2, name: "اردیبهشت", days: 31 },
  { id: 3, name: "خرداد", days: 31 },
  { id: 4, name: "تیر", days: 31 },
  { id: 5, name: "مرداد", days: 31 },
  { id: 6, name: "شهریور", days: 31 },
  { id: 7, name: "مهر", days: 30 },
  { id: 8, name: "آبان", days: 30 },
  { id: 9, name: "آذر", days: 30 },
  { id: 10, name: "دی", days: 30 },
  { id: 11, name: "بهمن", days: 30 },
  { id: 12, name: "اسفند", days: 29 },
];

const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const reasonLabelMap = {
  SALON_HOLIDAY: "تعطیلی سالن",
  STAFF_HOLIDAY: "تعطیلی مژه‌کار",
  STAFF_NOT_WORKING: "روز کاری نیست",
  FULLY_BOOKED: "ظرفیت تکمیل",
};

const isLeapYear = (year) => {
  const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
  return breaks.includes(year % 33);
};

const getDaysInMonth = (month, year) => {
  if (!month || month < 1 || month > 12) return 30;
  if (month === 12 && isLeapYear(year)) return 30;
  return persianMonths[month - 1].days;
};

const toDateOnly = (value) => (value ? String(value).split("T")[0] : "");

const EditVipDatePickerStep = ({
  axiosAdmin,
  selectedService,
  selectedType,
  selectedMethod,
  selectedStaff,
  appointmentDateTime, // { date: "2026-06-27", time: "08:00" }
  onDateTimeChange, // 👈 callback جدید برای اطلاع‌رسانی تغییرات
}) => {
  // ----- state for availability check -----
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState("");
  const [availabilityData, setAvailabilityData] = useState(null);

  // ----- internal selected date & time -----
  const [internalSelectedDate, setInternalSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  // ----- day detail state -----
  const [dayDetail, setDayDetail] = useState(null);
  const [fetchingDay, setFetchingDay] = useState(false);

  // ----- calendar navigation -----
  const [selectedYear, setSelectedYear] = useState(moment().jYear());
  const [selectedMonth, setSelectedMonth] = useState(moment().jMonth() + 1);

  const containerRef = useRef(null);
  const timeSectionRef = useRef(null);

  const staffId = selectedStaff?._id;
  const origDateStr = appointmentDateTime?.date; // e.g., "2026-06-27"
  const origTime = appointmentDateTime?.time; // e.g., "08:00"

  // ----- initial jalaali month/year derived from original appointment date -----
  useEffect(() => {
    if (origDateStr) {
      const m = moment(origDateStr, "YYYY-MM-DD");
      if (m.isValid()) {
        setSelectedYear(m.jYear());
        setSelectedMonth(m.jMonth() + 1);
      }
    }
  }, [origDateStr]);

  // ----- fetch availability data on mount -----
  useEffect(() => {
    if (!selectedService || !selectedType || !selectedMethod || !selectedStaff) return;
    const fetchAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError("");
      try {
        const res = await axiosAdmin.post("/api/admin/appointment/check-availability", {
          serviceId: selectedService._id,
          type: selectedType,
          method: selectedMethod,
          staffId: selectedStaff._id,
          days: 180,
        });
        setAvailabilityData(res.data);
      } catch (err) {
        const msg = err?.response?.data?.message || "خطا در بررسی ظرفیت رزرو VIP";
        setAvailabilityError(msg);
        toast.error(msg);
      } finally {
        setAvailabilityLoading(false);
      }
    };
    fetchAvailability();
  }, [axiosAdmin, selectedService, selectedType, selectedMethod, selectedStaff]);

  // ----- build maps from availability data -----
  const availableMap = useMemo(() => {
    const map = {};
    (availabilityData?.availability?.availableDates || []).forEach((item) => {
      map[toDateOnly(item.date)] = item;
    });
    return map;
  }, [availabilityData]);

  const unavailableMap = useMemo(() => {
    const map = {};
    (availabilityData?.availability?.unavailableDates || []).forEach((item) => {
      map[toDateOnly(item.date)] = item;
    });
    return map;
  }, [availabilityData]);

  const serverToday = availabilityData?.dateNowServer?.today || moment().format("YYYY-MM-DD");
  const serverTime = availabilityData?.dateNowServer?.serverTime;

  // ----- auto-select the original date after availability loads -----
  useEffect(() => {
    if (!origDateStr || !availabilityData) return;
    const jalaaliDate = moment(origDateStr, "YYYY-MM-DD").format("jYYYY/jMM/jDD");
    const availableItem = availableMap[origDateStr];
    const datePayload = {
      date: origDateStr,
      jalaaliDate,
      ...(availableItem || {}),
    };
    setInternalSelectedDate(datePayload);
  }, [origDateStr, availabilityData, availableMap]);

  // ----- fetch day availability whenever selected date changes -----
  useEffect(() => {
    if (!internalSelectedDate || !staffId || !axiosAdmin) return;
    const fetchDay = async () => {
      setFetchingDay(true);
      setDayDetail(null);
      try {
        const res = await axiosAdmin.get("/api/admin/appointment/day-availability", {
          params: { staffId, date: internalSelectedDate.date },
        });
        setDayDetail(res.data);
      } catch (err) {
        toast.error("خطا در دریافت جزئیات روز");
      } finally {
        setFetchingDay(false);
      }
    };
    fetchDay();
  }, [internalSelectedDate, staffId, axiosAdmin]);

  // ----- logic to handle original time ownership -----
  const augmentedTimeSlots = useMemo(() => {
    if (!dayDetail || !internalSelectedDate?.date) return [];
    let { workTimes, bookedTimes } = dayDetail;

    if (internalSelectedDate.date === origDateStr && origTime) {
      bookedTimes = bookedTimes.filter((t) => t !== origTime);
    }

    const now = serverTime ? new Date(serverTime) : new Date();
    const selectedDayDate = internalSelectedDate.date;

    return workTimes.map((time) => {
      const isBooked = bookedTimes.includes(time);
      let isPast = false;
      if (selectedDayDate && time) {
        const d = new Date(selectedDayDate + "T" + time + ":00");
        if (d.toDateString() === now.toDateString()) {
          isPast = d.getTime() < now.getTime();
        }
      }
      return {
        time,
        isBooked,
        isPast,
        disabled: isBooked || isPast,
      };
    });
  }, [dayDetail, serverTime, internalSelectedDate, origDateStr, origTime]);

  // ----- auto-select original time when on original date and day detail loaded -----
  useEffect(() => {
    if (
      origTime &&
      internalSelectedDate?.date === origDateStr &&
      augmentedTimeSlots.length > 0
    ) {
      const slot = augmentedTimeSlots.find((s) => s.time === origTime);
      if (slot && !slot.disabled) {
        setSelectedTime(origTime);
      } else {
        setSelectedTime(null);
      }
    } else {
      setSelectedTime(null);
    }
  }, [augmentedTimeSlots, internalSelectedDate, origDateStr, origTime]);

  // ----- calendar generation -----
  const calendarDays = useMemo(() => {
    if (!selectedMonth || !selectedYear) return [];
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDayOfMonth = moment(`${selectedYear}/${selectedMonth}/1`, "jYYYY/jM/jD");
    const startDayOfWeek = firstDayOfMonth.day() + 1;
    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const jalaaliDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD").format("jYYYY/jMM/jDD");
      const gregorianDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD").format("YYYY-MM-DD");
      const availableItem = availableMap[gregorianDate];
      const unavailableItem = unavailableMap[gregorianDate];
      days.push({
        day,
        jalaaliDate,
        gregorianDate,
        isToday: gregorianDate === serverToday,
        isAvailable: Boolean(availableItem),
        isUnavailable: Boolean(unavailableItem),
        isSelected: internalSelectedDate?.date === gregorianDate,
        availableItem,
        unavailableItem,
      });
    }
    return days;
  }, [selectedMonth, selectedYear, availableMap, unavailableMap, serverToday, internalSelectedDate]);

  const currentMonthName = persianMonths[selectedMonth - 1]?.name || "نامشخص";

  const changeMonth = (dir) => {
    if (dir === "next") {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear((y) => y + 1);
      } else {
        setSelectedMonth((m) => m + 1);
      }
    } else {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear((y) => y - 1);
      } else {
        setSelectedMonth((m) => m - 1);
      }
    }
  };

  // ✅ تابع مشترک برای اطلاع‌رسانی به والد
  const notifyParent = useCallback(
    (date, time) => {
      if (date && time && onDateTimeChange) {
        onDateTimeChange({
          date: date.date,
          jalaaliDate: date.jalaaliDate,
          time,
        });
      }
    },
    [onDateTimeChange]
  );

  // ----- handle date selection (با notifyParent) -----
  const handleSelectDay = (dateObj) => {
    if (!dateObj || !dateObj.isAvailable) return;
    const payload = {
      date: dateObj.gregorianDate,
      jalaaliDate: dateObj.jalaaliDate,
      ...dateObj.availableItem,
    };
    setInternalSelectedDate(payload);
    // اگر تایم از قبل انتخاب شده بود، به والد اطلاع بده
    if (selectedTime) {
      notifyParent(payload, selectedTime);
    }
    setTimeout(() => {
      timeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // ----- handle time selection (با notifyParent) -----
  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    if (internalSelectedDate) {
      notifyParent(internalSelectedDate, time);
    }
  };

  const getUnavailableReasonText = (dateObj) => {
    const reasons = dateObj?.unavailableItem?.reasons || [];
    if (!reasons.length) return "غیرقابل رزرو";
    return reasons.map((r) => reasonLabelMap[r.code] || r.message).join("، ");
  };

  // ----- render -----
  if (availabilityLoading) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-800 mb-2">در حال بررسی ظرفیت رزرو</h3>
          <p className="text-sm text-slate-500">لطفاً چند لحظه صبر کنید...</p>
        </div>
      </div>
    );
  }

  if (availabilityError) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-red-700 mb-2">خطا در دریافت ظرفیت</h3>
          <p className="text-sm text-red-600 leading-7">{availabilityError}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-600 to-fuchsia-600 rounded-2xl p-5 shadow-lg shadow-purple-500/25 relative overflow-hidden">
        <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 -right-16 w-44 h-44 rounded-full bg-white/10" />
        <div className="relative z-10 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-white" />
            <h3 className="text-xl font-bold text-white">ویرایش تاریخ و ساعت VIP</h3>
          </div>
          <p className="text-purple-100 text-sm">
            تاریخ جدید را انتخاب کنید. تاریخ فعلی قرار به‌صورت پیش‌فرض انتخاب شده است.
          </p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white max-w-2xl mx-auto border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Calendar Header */}
        <div className="bg-slate-900 px-4 md:px-5 py-4 flex items-center justify-between">
          <button
            onClick={() => changeMonth("prev")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
          <div className="text-center">
            <h3 className="text-lg md:text-xl font-black text-white">
              {currentMonthName} {selectedYear}
            </h3>
          </div>
          <button
            onClick={() => changeMonth("next")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Week Days */}
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {weekDays.map((day) => (
            <div key={day} className="py-2 md:py-3 text-center text-xs md:text-sm font-bold text-slate-600">
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1 md:gap-2 p-2 md:p-4 bg-white">
          {calendarDays.map((dateObj, index) => {
            if (!dateObj) return <div key={`empty-${index}`} className="aspect-square" />;
            const disabled = !dateObj.isAvailable;
            return (
              <button
                key={dateObj.gregorianDate}
                type="button"
                disabled={disabled}
                onClick={() => handleSelectDay(dateObj)}
                title={
                  disabled
                    ? getUnavailableReasonText(dateObj)
                    : `قابل رزرو - ${dateObj.availableItem?.freeTimes?.length || 0} تایم آزاد`
                }
                className={`
                  relative aspect-square rounded-xl md:rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-0.5
                  text-[12px] md:text-base font-black
                  ${
                    dateObj.isSelected
                      ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
                      : dateObj.isAvailable
                      ? "border-green-200 bg-white hover:bg-green-50 hover:border-green-400 hover:shadow-md cursor-pointer"
                      : "border-slate-200 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
                  }
                `}
              >
                {dateObj.isToday && (
                  <span
                    className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                      dateObj.isSelected ? "bg-white" : "bg-blue-500"
                    }`}
                  />
                )}
                {disabled && (
                  <span className="absolute top-1 left-1">
                    <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-400" />
                  </span>
                )}
                {dateObj.isAvailable && !dateObj.isSelected && (
                  <span className="absolute top-1 left-1 hidden md:block">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  </span>
                )}
                <span className="text-base md:text-lg">{dateObj.day}</span>
                {!dateObj.isAvailable && (
                  <span className="hidden md:inline text-[9px] md:text-[10px] px-0.5 text-center leading-4 line-clamp-2">
                    {getUnavailableReasonText(dateObj)}
                  </span>
                )}
                {dateObj.isAvailable && (
                  <span
                    className={`text-[9px] md:text-[10px] font-medium ${
                      dateObj.isSelected ? "text-purple-100" : "text-green-600"
                    }`}
                  >
                    {dateObj.availableItem?.freeTimes?.length || 0} تایم
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* month navigation buttons */}
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={() => changeMonth("prev")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
          >
            <ArrowRight className="w-4 h-4" />
            <span>ماه قبل</span>
          </button>
          <button
            onClick={() => changeMonth("next")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
          >
            <span>ماه بعد</span>
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Time Selection */}
      {internalSelectedDate && (
        <div ref={timeSectionRef} className="space-y-4">
          <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl p-4">
            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <p className="text-sm font-semibold text-purple-800">
              تاریخ انتخاب‌شده: {internalSelectedDate.jalaaliDate}
            </p>
          </div>

          {fetchingDay ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
              <span className="mr-2 text-slate-600">دریافت ساعات...</span>
            </div>
          ) : dayDetail ? (
            dayDetail.isAvailable ? (
              augmentedTimeSlots.filter((s) => !s.disabled).length > 0 ? (
                <div>
                  <h4 className="text-sm font-bold text-slate-700 mb-3">انتخاب ساعت رزرو</h4>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {augmentedTimeSlots.map((slot) => {
                      const disabled = slot.disabled;
                      const isSelected = selectedTime === slot.time;
                      if (disabled) {
                        return (
                          <div
                            key={slot.time}
                            className="relative rounded-xl border border-slate-200 bg-slate-100 p-2 text-center opacity-60 cursor-not-allowed select-none"
                          >
                            <span className="text-sm font-semibold text-slate-400 line-through">
                              {slot.time}
                            </span>
                            {slot.isBooked && (
                              <span className="absolute top-1 right-1">
                                <XCircle className="w-3 h-3 text-slate-400" />
                              </span>
                            )}
                            {slot.isPast && (
                              <div className="text-[10px] text-red-400 mt-0.5">گذشت</div>
                            )}
                          </div>
                        );
                      }
                      return (
                        <button
                          key={slot.time}
                          type="button"
                          onClick={() => handleTimeSelect(slot.time)} // 👈 از تابع جدید استفاده می‌کنیم
                          className={`relative rounded-xl border-2 p-2 text-center transition-all duration-200 ${
                            isSelected
                              ? "border-purple-600 bg-purple-50 shadow-md shadow-purple-200"
                              : "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
                          }`}
                        >
                          <span className={`text-sm font-bold ${isSelected ? "text-purple-700" : "text-slate-800"}`}>
                            {slot.time}
                          </span>
                          {isSelected && (
                            <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm text-amber-800 font-medium">
                    تایم‌های این روز تمام شده. روز دیگری انتخاب کنید.
                  </p>
                </div>
              )
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center">
                <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-700 font-medium">
                  این روز به دلایل زیر قابل رزرو نیست:
                </p>
                <ul className="mt-2 text-xs text-slate-600 list-disc list-inside">
                  {dayDetail.reasons?.map((r, idx) => (
                    <li key={idx}>{r.message}</li>
                  ))}
                </ul>
              </div>
            )
          ) : (
            <div className="text-center text-slate-500 py-4">
              اطلاعات ساعتی برای این روز در دسترس نیست.
            </div>
          )}
        </div>
      )}

      {/* ❌ فوتر با دکمه‌های بازگشت و ذخیره کاملاً حذف شده است */}
    </div>
  );
};

export default EditVipDatePickerStep;

// // components/EditVipDatePickerStep.jsx
// import { useMemo, useState, useEffect, useCallback, useRef } from "react";
// import moment from "moment-jalaali";
// import {
//   Calendar,
//   ChevronLeft,
//   ChevronRight,
//   Lock,
//   CheckCircle,
//   AlertCircle,
//   Loader2,
//   ArrowRight,
//   ArrowLeft,
//   XCircle,
//   Clock,
// } from "lucide-react";
// import { toast } from "react-toastify";

// const persianMonths = [
//   { id: 1, name: "فروردین", days: 31 },
//   { id: 2, name: "اردیبهشت", days: 31 },
//   { id: 3, name: "خرداد", days: 31 },
//   { id: 4, name: "تیر", days: 31 },
//   { id: 5, name: "مرداد", days: 31 },
//   { id: 6, name: "شهریور", days: 31 },
//   { id: 7, name: "مهر", days: 30 },
//   { id: 8, name: "آبان", days: 30 },
//   { id: 9, name: "آذر", days: 30 },
//   { id: 10, name: "دی", days: 30 },
//   { id: 11, name: "بهمن", days: 30 },
//   { id: 12, name: "اسفند", days: 29 },
// ];

// const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

// const reasonLabelMap = {
//   SALON_HOLIDAY: "تعطیلی سالن",
//   STAFF_HOLIDAY: "تعطیلی مژه‌کار",
//   STAFF_NOT_WORKING: "روز کاری نیست",
//   FULLY_BOOKED: "ظرفیت تکمیل",
// };

// const isLeapYear = (year) => {
//   const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
//   return breaks.includes(year % 33);
// };

// const getDaysInMonth = (month, year) => {
//   if (!month || month < 1 || month > 12) return 30;
//   if (month === 12 && isLeapYear(year)) return 30;
//   return persianMonths[month - 1].days;
// };

// const toDateOnly = (value) => (value ? String(value).split("T")[0] : "");

// const EditVipDatePickerStep = ({
//     axiosAdmin,
//   selectedService,
//   selectedType,
//   selectedMethod,
//   selectedStaff,
//   appointmentDateTime, // { date: "2026-06-27", time: "08:00" }
//   onDateTimeChange, // 👈 callback جدید برای اطلاع‌رسانی تغییرات
// }) => {
//   // ----- state for availability check -----
//   const [availabilityLoading, setAvailabilityLoading] = useState(true);
//   const [availabilityError, setAvailabilityError] = useState("");
//   const [availabilityData, setAvailabilityData] = useState(null);

//   // ----- internal selected date & time -----
//   const [internalSelectedDate, setInternalSelectedDate] = useState(null);
//   const [selectedTime, setSelectedTime] = useState(null);

//   // ----- day detail state -----
//   const [dayDetail, setDayDetail] = useState(null);
//   const [fetchingDay, setFetchingDay] = useState(false);

//   // ----- calendar navigation -----
//   const [selectedYear, setSelectedYear] = useState(moment().jYear());
//   const [selectedMonth, setSelectedMonth] = useState(moment().jMonth() + 1);

//   const containerRef = useRef(null);
//   const timeSectionRef = useRef(null);

//   const staffId = selectedStaff?._id;
//   const origDateStr = appointmentDateTime?.date; // e.g., "2026-06-27"
//   const origTime = appointmentDateTime?.time; // e.g., "08:00"

//   // ----- initial jalaali month/year derived from original appointment date -----
//   useEffect(() => {
//     if (origDateStr) {
//       const m = moment(origDateStr, "YYYY-MM-DD");
//       if (m.isValid()) {
//         setSelectedYear(m.jYear());
//         setSelectedMonth(m.jMonth() + 1);
//       }
//     }
//   }, [origDateStr]);

//   // ----- fetch availability data on mount -----
//   useEffect(() => {
//     if (!selectedService || !selectedType || !selectedMethod || !selectedStaff) return;
//     const fetchAvailability = async () => {
//       setAvailabilityLoading(true);
//       setAvailabilityError("");
//       try {
//         const res = await axiosAdmin.post("/api/admin/appointment/check-availability", {
//           serviceId: selectedService._id,
//           type: selectedType,
//           method: selectedMethod,
//           staffId: selectedStaff._id,
//           days: 180,
//         });
//         setAvailabilityData(res.data);
//       } catch (err) {
//         const msg = err?.response?.data?.message || "خطا در بررسی ظرفیت رزرو VIP";
//         setAvailabilityError(msg);
//         toast.error(msg);
//       } finally {
//         setAvailabilityLoading(false);
//       }
//     };
//     fetchAvailability();
//   }, [axiosAdmin, selectedService, selectedType, selectedMethod, selectedStaff]);

//   // ----- build maps from availability data -----
//   const availableMap = useMemo(() => {
//     const map = {};
//     (availabilityData?.availability?.availableDates || []).forEach((item) => {
//       map[toDateOnly(item.date)] = item;
//     });
//     return map;
//   }, [availabilityData]);

//   const unavailableMap = useMemo(() => {
//     const map = {};
//     (availabilityData?.availability?.unavailableDates || []).forEach((item) => {
//       map[toDateOnly(item.date)] = item;
//     });
//     return map;
//   }, [availabilityData]);

//   const serverToday = availabilityData?.dateNowServer?.today || moment().format("YYYY-MM-DD");
//   const serverTime = availabilityData?.dateNowServer?.serverTime;

//   // ----- auto-select the original date after availability loads -----
//   useEffect(() => {
//     if (!origDateStr || !availabilityData) return;
//     const jalaaliDate = moment(origDateStr, "YYYY-MM-DD").format("jYYYY/jMM/jDD");
//     const availableItem = availableMap[origDateStr];
//     const datePayload = {
//       date: origDateStr,
//       jalaaliDate,
//       ...(availableItem || {}),
//     };
//     setInternalSelectedDate(datePayload);
//     // Optionally notify parent? Not needed.
//   }, [origDateStr, availabilityData, availableMap]);

//   // ----- fetch day availability whenever selected date changes -----
//   useEffect(() => {
//     if (!internalSelectedDate || !staffId || !axiosAdmin) return;
//     const fetchDay = async () => {
//       setFetchingDay(true);
//       setDayDetail(null);
//       try {
//         const res = await axiosAdmin.get("/api/admin/appointment/day-availability", {
//           params: { staffId, date: internalSelectedDate.date },
//         });
//         setDayDetail(res.data);
//       } catch (err) {
//         toast.error("خطا در دریافت جزئیات روز");
//       } finally {
//         setFetchingDay(false);
//       }
//     };
//     fetchDay();
//   }, [internalSelectedDate, staffId, axiosAdmin]);

//   // ----- logic to handle original time ownership -----
//   // When on the original appointment date, we must ensure the original time is not listed as booked.
//   const augmentedTimeSlots = useMemo(() => {
//     if (!dayDetail || !internalSelectedDate?.date) return [];
//     let { workTimes, bookedTimes } = dayDetail;

//     // If we're on the original appointment date, remove the original time from bookedTimes (if present)
//     // so it becomes selectable again.
//     if (internalSelectedDate.date === origDateStr && origTime) {
//       bookedTimes = bookedTimes.filter((t) => t !== origTime);
//     }

//     const now = serverTime ? new Date(serverTime) : new Date();
//     const selectedDayDate = internalSelectedDate.date;

//     return workTimes.map((time) => {
//       const isBooked = bookedTimes.includes(time);
//       let isPast = false;
//       if (selectedDayDate && time) {
//         const d = new Date(selectedDayDate + "T" + time + ":00");
//         if (d.toDateString() === now.toDateString()) {
//           isPast = d.getTime() < now.getTime();
//         }
//       }
//       return {
//         time,
//         isBooked,
//         isPast,
//         disabled: isBooked || isPast,
//       };
//     });
//   }, [dayDetail, serverTime, internalSelectedDate, origDateStr, origTime]);

//   // ----- auto-select original time when on original date and day detail loaded -----
//   useEffect(() => {
//     if (
//       origTime &&
//       internalSelectedDate?.date === origDateStr &&
//       augmentedTimeSlots.length > 0
//     ) {
//       // Check if the original time is available (not disabled)
//       const slot = augmentedTimeSlots.find((s) => s.time === origTime);
//       if (slot && !slot.disabled) {
//         setSelectedTime(origTime);
//       } else {
//         // If it's disabled (shouldn't happen because we removed it, but just in case)
//         setSelectedTime(null);
//       }
//     } else {
//       // If we're not on the original date, clear time selection.
//       setSelectedTime(null);
//     }
//   }, [augmentedTimeSlots, internalSelectedDate, origDateStr, origTime]);

//   // ----- calendar generation -----
//   const calendarDays = useMemo(() => {
//     if (!selectedMonth || !selectedYear) return [];
//     const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
//     const firstDayOfMonth = moment(`${selectedYear}/${selectedMonth}/1`, "jYYYY/jM/jD");
//     const startDayOfWeek = firstDayOfMonth.day() + 1;
//     const days = [];
//     for (let i = 0; i < startDayOfWeek; i++) {
//       days.push(null);
//     }
//     for (let day = 1; day <= daysInMonth; day++) {
//       const jalaaliDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD").format("jYYYY/jMM/jDD");
//       const gregorianDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD").format("YYYY-MM-DD");
//       const availableItem = availableMap[gregorianDate];
//       const unavailableItem = unavailableMap[gregorianDate];
//       days.push({
//         day,
//         jalaaliDate,
//         gregorianDate,
//         isToday: gregorianDate === serverToday,
//         isAvailable: Boolean(availableItem),
//         isUnavailable: Boolean(unavailableItem),
//         isSelected: internalSelectedDate?.date === gregorianDate,
//         availableItem,
//         unavailableItem,
//       });
//     }
//     return days;
//   }, [selectedMonth, selectedYear, availableMap, unavailableMap, serverToday, internalSelectedDate]);

//   // ----- month name -----
//   const currentMonthName = persianMonths[selectedMonth - 1]?.name || "نامشخص";

//   const changeMonth = (dir) => {
//     if (dir === "next") {
//       if (selectedMonth === 12) {
//         setSelectedMonth(1);
//         setSelectedYear((y) => y + 1);
//       } else {
//         setSelectedMonth((m) => m + 1);
//       }
//     } else {
//       if (selectedMonth === 1) {
//         setSelectedMonth(12);
//         setSelectedYear((y) => y - 1);
//       } else {
//         setSelectedMonth((m) => m - 1);
//       }
//     }
//   };

//    const notifyParent = useCallback(
//     (date, time) => {
//       if (date && time && onDateTimeChange) {
//         onDateTimeChange({
//           date: date.date,
//           jalaaliDate: date.jalaaliDate,
//           time,
//         });
//       }
//     },
//     [onDateTimeChange]
//   );

//   // ----- handle date selection -----
//   const handleSelectDay = (dateObj) => {
//     if (!dateObj || !dateObj.isAvailable) return;
//     const payload = {
//       date: dateObj.gregorianDate,
//       jalaaliDate: dateObj.jalaaliDate,
//       ...dateObj.availableItem,
//     };
//     setInternalSelectedDate(payload);


//      // اگر تایم از قبل انتخاب شده بود، به والد اطلاع بده
//     if (selectedTime) {
//       notifyParent(payload, selectedTime);
//     }

//     // scroll to time section after a tick
//     setTimeout(() => {
//       timeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
//     }, 100);
//   };

//    // ----- handle time selection (با notifyParent) -----
//   const handleTimeSelect = (time) => {
//     setSelectedTime(time);
//     if (internalSelectedDate) {
//       notifyParent(internalSelectedDate, time);
//     }
//   };


//   // ----- continue action -----
//   const handleContinue = () => {
//     if (!internalSelectedDate || !selectedTime) return;
//     onContinue({
//       date: internalSelectedDate.date,
//       jalaaliDate: internalSelectedDate.jalaaliDate,
//       time: selectedTime,
//     });
//   };

//   const getUnavailableReasonText = (dateObj) => {
//     const reasons = dateObj?.unavailableItem?.reasons || [];
//     if (!reasons.length) return "غیرقابل رزرو";
//     return reasons.map((r) => reasonLabelMap[r.code] || r.message).join("، ");
//   };

//   // ----- render -----
//   if (availabilityLoading) {
//     return (
//       <div className="min-h-[500px] flex items-center justify-center">
//         <div className="text-center">
//           <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
//           <h3 className="text-lg font-bold text-slate-800 mb-2">در حال بررسی ظرفیت رزرو</h3>
//           <p className="text-sm text-slate-500">لطفاً چند لحظه صبر کنید...</p>
//         </div>
//       </div>
//     );
//   }

//   if (availabilityError) {
//     return (
//       <div className="min-h-[500px] flex items-center justify-center">
//         <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
//           <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
//           <h3 className="text-lg font-bold text-red-700 mb-2">خطا در دریافت ظرفیت</h3>
//           <p className="text-sm text-red-600 leading-7">{availabilityError}</p>
//           <button
//             onClick={onBack}
//             className="mt-5 px-5 py-2.5 rounded-xl bg-white border border-red-200 text-red-600 hover:bg-red-100"
//           >
//             بازگشت
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div ref={containerRef} className="space-y-6">
//       {/* Header */}
//       <div className="bg-gradient-to-br from-purple-600 via-purple-600 to-fuchsia-600 rounded-2xl p-5 shadow-lg shadow-purple-500/25 relative overflow-hidden">
//         <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full bg-white/10" />
//         <div className="absolute -bottom-16 -right-16 w-44 h-44 rounded-full bg-white/10" />
//         <div className="relative z-10 flex flex-col gap-2">
//           <div className="flex items-center gap-2">
//             <Calendar className="w-6 h-6 text-white" />
//             <h3 className="text-xl font-bold text-white">ویرایش تاریخ و ساعت VIP</h3>
//           </div>
//           <p className="text-purple-100 text-sm">
//             تاریخ جدید را انتخاب کنید. تاریخ فعلی قرار به‌صورت پیش‌فرض انتخاب شده است.
//           </p>
//         </div>
//       </div>

//       {/* Calendar */}
//       <div className="bg-white max-w-2xl mx-auto border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
//         {/* Calendar Header */}
//         <div className="bg-slate-900 px-4 md:px-5 py-4 flex items-center justify-between">
//           <button
//             onClick={() => changeMonth("prev")}
//             className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center"
//           >
//             <ChevronRight className="w-5 h-5 text-white" />
//           </button>
//           <div className="text-center">
//             <h3 className="text-lg md:text-xl font-black text-white">
//               {currentMonthName} {selectedYear}
//             </h3>
//           </div>
//           <button
//             onClick={() => changeMonth("next")}
//             className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center"
//           >
//             <ChevronLeft className="w-5 h-5 text-white" />
//           </button>
//         </div>

//         {/* Week Days */}
//         <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
//           {weekDays.map((day) => (
//             <div key={day} className="py-2 md:py-3 text-center text-xs md:text-sm font-bold text-slate-600">
//               {day}
//             </div>
//           ))}
//         </div>

//         {/* Days Grid */}
//         <div className="grid grid-cols-7 gap-1 md:gap-2 p-2 md:p-4 bg-white">
//           {calendarDays.map((dateObj, index) => {
//             if (!dateObj) return <div key={`empty-${index}`} className="aspect-square" />;
//             const disabled = !dateObj.isAvailable;
//             return (
//               <button
//                 key={dateObj.gregorianDate}
//                 type="button"
//                 disabled={disabled}
//                 onClick={() => handleSelectDay(dateObj)}
//                 title={
//                   disabled
//                     ? getUnavailableReasonText(dateObj)
//                     : `قابل رزرو - ${dateObj.availableItem?.freeTimes?.length || 0} تایم آزاد`
//                 }
//                 className={`
//                   relative aspect-square rounded-xl md:rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-0.5
//                   text-[12px] md:text-base font-black
//                   ${
//                     dateObj.isSelected
//                       ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
//                       : dateObj.isAvailable
//                       ? "border-green-200 bg-white hover:bg-green-50 hover:border-green-400 hover:shadow-md cursor-pointer"
//                       : "border-slate-200 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
//                   }
//                 `}
//               >
//                 {dateObj.isToday && (
//                   <span
//                     className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
//                       dateObj.isSelected ? "bg-white" : "bg-blue-500"
//                     }`}
//                   />
//                 )}
//                 {disabled && (
//                   <span className="absolute top-1 left-1">
//                     <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-400" />
//                   </span>
//                 )}
//                 {dateObj.isAvailable && !dateObj.isSelected && (
//                   <span className="absolute top-1 left-1 hidden md:block">
//                     <CheckCircle className="w-3.5 h-3.5 text-green-500" />
//                   </span>
//                 )}
//                 <span className="text-base md:text-lg">{dateObj.day}</span>
//                 {!dateObj.isAvailable && (
//                   <span className="hidden md:inline text-[9px] md:text-[10px] px-0.5 text-center leading-4 line-clamp-2">
//                     {getUnavailableReasonText(dateObj)}
//                   </span>
//                 )}
//                 {dateObj.isAvailable && (
//                   <span
//                     className={`text-[9px] md:text-[10px] font-medium ${
//                       dateObj.isSelected ? "text-purple-100" : "text-green-600"
//                     }`}
//                   >
//                     {dateObj.availableItem?.freeTimes?.length || 0} تایم
//                   </span>
//                 )}
//               </button>
//             );
//           })}
//         </div>

//         {/* month navigation buttons */}
//         <div className="flex items-center justify-between px-4 md:px-5 py-3 border-t border-slate-100 bg-slate-50/50">
//           <button
//             onClick={() => changeMonth("prev")}
//             className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
//           >
//             <ArrowRight className="w-4 h-4" />
//             <span>ماه قبل</span>
//           </button>
//           <button
//             onClick={() => changeMonth("next")}
//             className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
//           >
//             <span>ماه بعد</span>
//             <ArrowLeft className="w-4 h-4" />
//           </button>
//         </div>
//       </div>

//       {/* Time Selection */}
//       {internalSelectedDate && (
//         <div ref={timeSectionRef} className="space-y-4">
//           <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl p-4">
//             <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
//             <p className="text-sm font-semibold text-purple-800">
//               تاریخ انتخاب‌شده: {internalSelectedDate.jalaaliDate}
//             </p>
//           </div>

//           {fetchingDay ? (
//             <div className="flex items-center justify-center py-8">
//               <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
//               <span className="mr-2 text-slate-600">دریافت ساعات...</span>
//             </div>
//           ) : dayDetail ? (
//             dayDetail.isAvailable ? (
//               augmentedTimeSlots.filter((s) => !s.disabled).length > 0 ? (
//                 <div>
//                   <h4 className="text-sm font-bold text-slate-700 mb-3">انتخاب ساعت رزرو</h4>
//                   <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
//                     {augmentedTimeSlots.map((slot) => {
//                       const disabled = slot.disabled;
//                       const isSelected = selectedTime === slot.time;
//                       if (disabled) {
//                         return (
//                           <div
//                             key={slot.time}
//                             className="relative rounded-xl border border-slate-200 bg-slate-100 p-2 text-center opacity-60 cursor-not-allowed select-none"
//                           >
//                             <span className="text-sm font-semibold text-slate-400 line-through">
//                               {slot.time}
//                             </span>
//                             {slot.isBooked && (
//                               <span className="absolute top-1 right-1">
//                                 <XCircle className="w-3 h-3 text-slate-400" />
//                               </span>
//                             )}
//                             {slot.isPast && (
//                               <div className="text-[10px] text-red-400 mt-0.5">گذشت</div>
//                             )}
//                           </div>
//                         );
//                       }
//                       return (
//                         <button
//                           key={slot.time}
//                           type="button"
//                           onClick={() => setSelectedTime(slot.time)}
//                           className={`relative rounded-xl border-2 p-2 text-center transition-all duration-200 ${
//                             isSelected
//                               ? "border-purple-600 bg-purple-50 shadow-md shadow-purple-200"
//                               : "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
//                           }`}
//                         >
//                           <span className={`text-sm font-bold ${isSelected ? "text-purple-700" : "text-slate-800"}`}>
//                             {slot.time}
//                           </span>
//                           {isSelected && (
//                             <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">
//                               ✓
//                             </span>
//                           )}
//                         </button>
//                       );
//                     })}
//                   </div>
//                 </div>
//               ) : (
//                 <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
//                   <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
//                   <p className="text-sm text-amber-800 font-medium">
//                     تایم‌های این روز تمام شده. روز دیگری انتخاب کنید.
//                   </p>
//                 </div>
//               )
//             ) : (
//               <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center">
//                 <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
//                 <p className="text-sm text-slate-700 font-medium">
//                   این روز به دلایل زیر قابل رزرو نیست:
//                 </p>
//                 <ul className="mt-2 text-xs text-slate-600 list-disc list-inside">
//                   {dayDetail.reasons?.map((r, idx) => (
//                     <li key={idx}>{r.message}</li>
//                   ))}
//                 </ul>
//               </div>
//             )
//           ) : (
//             <div className="text-center text-slate-500 py-4">
//               اطلاعات ساعتی برای این روز در دسترس نیست.
//             </div>
//           )}
//         </div>
//       )}

//       {/* Footer buttons */}
//       {/* <div className="flex items-center justify-between pt-2">
//         <button
//           type="button"
//           onClick={onBack}
//           className="xm:px-5 px-2 py-2.5 rounded-xl xm:text-base text-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
//         >
//           <ArrowRight className="w-4 h-4" />
//           بازگشت
//         </button>

//         <button
//           type="button"
//           disabled={!internalSelectedDate || !selectedTime}
//           onClick={handleContinue}
//           className="xm:px-6 px-3 py-2.5 xm:text-base text-sm rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
//         >
//           <span className="45xm:block hidden">ذخیره تاریخ و ساعت</span>
//           <span className="45xm:hidden block">ذخیره</span>
//           <CheckCircle className="w-4 h-4" />
//         </button>
//       </div> */}
//     </div>
//   );
// };

// export default EditVipDatePickerStep;